import { createContext } from 'react';

interface SmartVideoObserver {
  observe: (video: HTMLVideoElement) => void;
  unobserve: (video: HTMLVideoElement) => void;
}

interface ObservedVideoData {
  video: HTMLVideoElement;
  timerRef: number;
}

const loadObserverOptions = {
  root: null, // Uses viewport
  rootMargin: '0px',
  threshold: 0, // Target element fully visible
};

const playObserverOptions = {
  root: null, // Uses viewport
  rootMargin: '0px',
  threshold: 1.0, // Target element fully visible
};

const VIDEO_AUTOPLAY_DELAY_MS = 100;
const TIMER_REF_INIT_VALUE = -1;

const smartVideoIntersectionObserver = (): SmartVideoObserver => {
  let counter = 0;
  const videoDataDict: Record<string, ObservedVideoData> = {};

  const playObserver = new IntersectionObserver((entries, _) => {
    entries.forEach((entry) => {
      const video = entry.target as HTMLVideoElement;

      if (entry.isIntersecting) {
        video.play();
      } else {
        video.pause();
      }
    });
  }, loadObserverOptions);

  const loadObserver = new IntersectionObserver((entries, _) => {
    entries.forEach((entry) => {
      const video = entry.target as HTMLVideoElement;

      if (entry.isIntersecting) {
        video.src = video.dataset.src ?? '';
        loadObserver.unobserve(video);

        const [index] = Object.entries(videoDataDict).find(
          ([_, v]) => v.video === video
        ) as [string, ObservedVideoData];

        const timer = window.setTimeout(
          () => playObserver.observe(video),
          VIDEO_AUTOPLAY_DELAY_MS
        );

        videoDataDict[index].timerRef = timer;
      }
    });
  }, playObserverOptions);

  const observe = (video: HTMLVideoElement) => {
    videoDataDict[counter++] = {
      video: video,
      timerRef: TIMER_REF_INIT_VALUE,
    };

    loadObserver.observe(video);
  };
  const unobserve = (video: HTMLVideoElement) => {
    const playObserverSubscriptionTimer = Object.entries(videoDataDict).find(
      ([_, videoData]) => videoData.video === video
    );
    if (playObserverSubscriptionTimer != null) {
      const [index, videoData] = playObserverSubscriptionTimer;
      window.clearTimeout(videoData.timerRef);
      delete videoDataDict[index];
    }

    loadObserver.unobserve(video);
    playObserver.unobserve(video);
  };

  return {
    observe,
    unobserve,
  };
};

const SmartVideoObserverContext = createContext<SmartVideoObserver>(
  smartVideoIntersectionObserver()
);

export default SmartVideoObserverContext;
